import React, { useEffect, useState, useMemo } from 'react';
import { Redirect } from 'react-router-dom';
import PropTypes from 'prop-types';
import { connect } from 'ljit-store-connecter';
import LoadingPageStatus from '../components/loading-page-status';
import {
	LoadingStatusEnum,
	MaintenanceStatusEnums,
	PlatformStatusEnums,
} from '../../lib/enums';
import { renderSwitches } from '../../lib/route-utils';
import {
	MaintenancePropType,
	PlatformStatusPropType,
} from '../../lib/prop-types-utils';
import { layoutRegistry } from '../../lib/ui-theme/layout-registry';
import { usePrevious } from '../../lib/react-utils';
import { AssetKeys } from '../../lib/ui-theme/assets/asset-keys';
import {
	serviceSettingActions,
	conversationActions,
} from '../controller';
import routes, {
	RouteKeyEnums,
	checkIsSystemRoutePath,
	SYSTEM_ROUTE_KEYS,
} from '../routes';

const { LOADING, SUCCESS, NONE, FAILED } = LoadingStatusEnum;
const { OFFLINE } = MaintenanceStatusEnums;
const { BLOCKED } = PlatformStatusEnums;
const { ENTRIES, MAIN, MAINTAIN, NOT_FOUND, LOGIN, ANONYMOUS_LOGIN, CONVERSATION_OVER } = RouteKeyEnums;
const { fetchServiceSettingAction } = serviceSettingActions;
const { fetchConversationAction } = conversationActions;
const propTypes = {
	auth: PropTypes.object,
	serviceSetting: MaintenancePropType,
	platformSetting: PropTypes.shape({
		maintenance: MaintenancePropType,
	}),
	platformStatus: PlatformStatusPropType,
	fetchServiceSettingAction: PropTypes.func.isRequired,
	fetchConversationAction: PropTypes.func.isRequired,
	conversationLoadingStatus: PropTypes.oneOf(Object.values(LoadingStatusEnum)).isRequired,
	meHasClosedConversation: PropTypes.bool,
};
const defaultProps = {
	auth: {},
};

function LayoutRoute({
	auth: {
		isAuthed = false,
		location: { pathname = '', search = '' } = {},
	} = {},
	serviceSetting: {
		status: serviceStatus,
	} = {},
	platformSetting: {
		maintenance: {
			status: platformMaintainStatus,
		} = {},
	} = {},
	platformStatus: {
		status: platformStatus,
	} = {},
	conversationLoadingStatus,
	meHasClosedConversation,
	fetchServiceSettingAction,
	fetchConversationAction,
}) {
	const Layout1 = layoutRegistry.layout1;
	const [isClosedConversation, setIsClosedConversation] = useState(false);
	const prevConversationLoadingStatus = usePrevious(conversationLoadingStatus);
	const prevIsAuthed = usePrevious(isAuthed);
	// --- 邏輯運算 (與 getDerivedStateFromProps 一致) ---
	const isDataChecking = conversationLoadingStatus === NONE || conversationLoadingStatus === LOADING;
	const isAllDataReady = conversationLoadingStatus === SUCCESS;
	// 當前是 FAILED，或者「剛從 LOADING 結束但不是 SUCCESS」
	const isAllDataCheckingFailMoment = conversationLoadingStatus === FAILED ||
		(prevConversationLoadingStatus === LOADING && conversationLoadingStatus !== SUCCESS);
	const { couldLoginViaId } = window.config;
	const isMaintainEnablePath = !((pathname === NOT_FOUND || pathname === LOGIN) && !couldLoginViaId);
	const { authRoutes, appRoutes } = useMemo(() => {
		const a = routes.filter(r => SYSTEM_ROUTE_KEYS.includes(r.path));
		const b = routes.filter(r => !SYSTEM_ROUTE_KEYS.includes(r.path));

		return { authRoutes: a, appRoutes: b };
	}, []);

	// 原 componentDidMount
	useEffect(() => {
		if (meHasClosedConversation) {
			setIsClosedConversation(true);
			return;
		}

		fetchServiceSettingAction();

		if (isAuthed) {
			fetchConversationAction();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// 原 componentDidUpdate
	useEffect(() => {
		// 補抓條件：(NONE) 或 (FAILED 且不在 ENTRIES) 且已登入
		const shouldFetch = (
			conversationLoadingStatus === NONE ||
			(conversationLoadingStatus === FAILED && pathname !== ENTRIES)
		) && isAuthed;

		// 使用從 usePrevious 拿到的 prevAuthed 判斷登入狀態切換
		if (shouldFetch || (prevIsAuthed === false && isAuthed === true)) {
			fetchConversationAction();
		}
	}, [
		isAuthed,
		prevIsAuthed,
		pathname,
		conversationLoadingStatus,
		fetchConversationAction,
	]);

	// --- 外觀策略（Phase1/Phase2 可延伸）---
	// Phase1 目標：
	// 1) 全站（含 system pages / loading）都由 Layout1 接管背景與版型骨架
	// 2) system pages 先隱藏 chrome（topBar/marquee/bottom/input），但仍沿用同一 Layout1 背景策略
	// Phase2 目標：
	// 1) 後台回傳 themeId，useThemeAssets 依 themeId 回傳 assets/tokens
	// 2) system/app 可能使用不同背景 key（甚至不同 layout），但邏輯集中在這裡維護
	const isSystemPath = checkIsSystemRoutePath(pathname);
	// Phase1：背景 key 規則集中管理，後續要新增 BG_SYSTEM / BG_ENTRIES 等只改這裡
	// - login / anonymous-login：用 BG_LOGIN
	// - system pages（例如 /maintain /404 /conversation-over）：用 BG_SYSTEM
	// - 其他 app pages：用 BG_MAIN
	const backgroundKey = (pathname === LOGIN || pathname === ANONYMOUS_LOGIN) ?
		AssetKeys.BG_LOGIN :
		(isSystemPath ? AssetKeys.BG_SYSTEM : AssetKeys.BG_MAIN);
	const backgroundKeyMobile = (pathname === LOGIN || pathname === ANONYMOUS_LOGIN) ?
		AssetKeys.BG_LOGIN_MOBILE :
		(isSystemPath ? AssetKeys.BG_SYSTEM_MOBILE : AssetKeys.BG_MAIN_MOBILE);
	// Phase1：即使 loading 也包 Layout1，確保背景一致
	let mainNode = null;

	// --- Render Flow ---
	// 1. 維護判定
	if (
		pathname !== MAINTAIN &&
		(serviceStatus === OFFLINE || platformMaintainStatus === OFFLINE || platformStatus === BLOCKED) &&
		isMaintainEnablePath
	) {
		mainNode = (
			<Redirect
				to={{ pathname: MAINTAIN, state: { from: pathname } }}
			/>
		);
		// 2. couldLoginViaId 判定
	} else if (!couldLoginViaId && !isAuthed && pathname !== MAINTAIN) {
		const allow = pathname === NOT_FOUND ||
			pathname === CONVERSATION_OVER ||
			pathname === ANONYMOUS_LOGIN ||
			pathname === LOGIN;

		if (allow) {
			mainNode = renderSwitches(authRoutes);
		} else if (meHasClosedConversation) {
			// AuthRoute 的 `isAuthed` 因 me.hasClosedConversation 轉為 false；app 路由已由 AuthRoute
			// 導向結束頁，這裡只兜底 allow 清單以外的 system path（例如 /logout）
			mainNode = (
				<Redirect
					to={{ pathname: CONVERSATION_OVER, state: { from: pathname } }}
				/>
			);
		} else {
			mainNode = (
				<Redirect
					to={{
						pathname: NOT_FOUND,
						state: { from: pathname },
						search,
					}}
				/>
			);
		}
	} else if (pathname.includes('api')) {
		// 3. API Login 判定
		mainNode = (<Redirect to={{ pathname: MAIN, state: { from: pathname } }} />);
	} else if (!isAuthed) {
		// 4. 未登入：走 system/auth routes（login/anonymous/maintain/404/...）
		mainNode = renderSwitches(authRoutes);
	} else if (isDataChecking) {
		mainNode = (<LoadingPageStatus />);
	} else {
		// 5. 會話關閉判定
		const hasClosedConversation = window.config.hasClosedConversation || isClosedConversation;

		if (hasClosedConversation) {
			mainNode = (<Redirect to={{ pathname: CONVERSATION_OVER, state: { from: pathname } }} />);
		} else if (isAllDataCheckingFailMoment && pathname !== ENTRIES) {
			mainNode = (<Redirect to={{ pathname: ENTRIES, state: { from: pathname } }} />);
		} else if (isAllDataReady && pathname === ENTRIES) {
			mainNode = (<Redirect to={{ pathname: MAIN, state: { from: pathname } }} />);
		} else {
			mainNode = renderSwitches(appRoutes);
		}
	}

	// 預設渲染：全站都用 Layout1 包住（包含 /login /maintain /404 /conversation-over）
	return (
		<Layout1
			backgroundKey={backgroundKey}
			backgroundKeyMobile={backgroundKeyMobile}
			main={mainNode}
		/>
	);
}

LayoutRoute.propTypes = propTypes;
LayoutRoute.defaultProps = defaultProps;

function mapStateToProps(state) {
	return {
		serviceSetting: state.serviceSetting.get('data').toObject(),
		platformSetting: state.platformSetting.get('data').toObject(),
		platformStatus: state.platformSetting.get('platformStatus').toObject(),
		conversationLoadingStatus: state.conversation.get('loadingStatus'),
		meHasClosedConversation: state.auth.getIn(['me', 'hasClosedConversation']),
	};
}

function mapDispatchToProps(dispatch) {
	return {
		fetchServiceSettingAction: () => dispatch(fetchServiceSettingAction()),
		fetchConversationAction: () => dispatch(fetchConversationAction()),
	};
}

export default connect(mapStateToProps, mapDispatchToProps)(LayoutRoute);
